import { GetterTree, MutationTree, ActionTree, ActionContext, Module } from 'vuex';
import { Location } from 'vue-router';
import { RootState } from '../types';
import IdDocPhotos from '@/api/id-doc-photos';
import MnDocPhotos from '@/api/my-number-doc-photos';
import GpDocPhotos from '@/api/general-doc-photos';
import OpDocPhotos from '@/api/optional-doc-photos';
import FaceFrontPhotos from '@/api/face-front-photos';
import IdDocType from '@/resources/enum/IdDocType';
import MnDocType from '@/resources/enum/MnDocType';
import PhotoType from '@/resources/enum/PhotoType';
import ApiCancelError from '@/resources/error/ApiCancelError';
import { OsType, getOSType } from '@/util/device';
import RegionCode from '@/resources/enum/RegionCode';
import vuei18n from '@/i18n/index';

export enum FacingMode {
  User = 'user',
  Environment = 'environment'
}

export type ShootType =
  | 'none'
  | 'identity-document-front'
  | 'identity-document-diagonal'
  | 'identity-document-back'
  | 'mynumber-document-front'
  | 'mynumber-document-back'
  | 'general-purpose-document-page'
  | 'face-front'
  | 'face-action'
  | 'optional-document-page';

export interface CameraDevice {
  deviceId: string;
  label: string;
}

export type State = {
  idDocType: IdDocType;
  mnDocType: MnDocType;
  gpDocType: string;
  opDocType: string[];
  shootType: ShootType;

  gpDocPageCurr: number;
  gpDocPageLast: number;
  opDocTermCurr: number;
  opDocTermLast: number;
  opDocPageCurr: number;
  opDocPageLast: number;

  headerHeight: number;
  headerMarginHeight: number;
  shootFooterHeight: number;
  idDocAspectRatioCalc: number;
  idDocAspectRatioView: number;

  documentName: string;
  i18nKeyIntroductionTitleString: string;
  i18nKeyIntroductionCaptionHeaderString: string;
  i18nKeyIntroductionCaptionString: string;
  i18nKeyIntroductionImage1String: string;
  i18nKeyIntroductionImage2String: string;
  i18nKeyIntroductionImage3String: string;
  i18nKeyIntroductionImage4String: string;
  i18nKeyIntroductionMovieString: string;
  i18nKeyIntroductionDescriptionArray: string;
  i18nKeyShootTitleString: string;
  i18nKeyShootDescriptionArray: string;
  i18nKeyConfirmationListArray: string;
  i18nKeyErrorTitleString: string;

  pictureWidth: number;
  pictureHeight: number;
  displayWidth: number;
  displayHeight: number;
  clippingPictureWidth: number;
  clippingDisplayWidth: number;
  pictureIdDocCenter: number;
  displayIdDocCenter: number;

  isNotFound: boolean;
  isResize: boolean;
  isStart: boolean;
  isShooting: boolean;
  triggerShoot: boolean;

  triggerDetect: number;
  detectCount: number;
  isPermittedCameraAccess: boolean;
  isDetectableIdDoc: boolean;
  isDetectStop: boolean;
  isDetectStart: boolean;
  isDetectCapture: boolean;
  isDetectSuccess: boolean;
  detectWarningCode: string;

  videoDevices: CameraDevice[];
  actualFacingMode: VideoFacingModeEnum | 'none';
  // FacingModeごとに選択したカメラをおぼておくためにRecord
  selectedDeviceIndex: Record<FacingMode, number>;
  unavailableCameras: string[];
  showCameraModeModal: boolean;
  isCameraChanging: boolean;
  isRefreshRequesting: boolean;

  retryCount: number;
  photoID: { [key: string]: number };
  trimmedPhoto: { [key: string]: string };
};

function createState(): State {
  const state: State = {} as State;
  clearState(state);
  return state;
}

function clearState(state: State): void {
  state.idDocType = IdDocType.Unknown;
  state.mnDocType = MnDocType.Unknown;
  state.gpDocType = 'unknown';
  state.opDocType = [];
  state.shootType = 'none';
  state.gpDocPageCurr = 0;
  state.gpDocPageLast = 0;
  state.opDocTermCurr = 0;
  state.opDocTermLast = 0;
  state.opDocPageCurr = 0;
  state.opDocPageLast = 0;
  state.headerHeight = 0;
  state.headerMarginHeight = 0;
  state.shootFooterHeight = 0;
  state.idDocAspectRatioCalc = 1.0;
  state.idDocAspectRatioView = 1.0;
  state.documentName = '';
  state.i18nKeyIntroductionTitleString = '';
  state.i18nKeyIntroductionCaptionString = '';
  state.i18nKeyIntroductionCaptionHeaderString = '';
  state.i18nKeyIntroductionImage1String = '';
  state.i18nKeyIntroductionImage2String = '';
  state.i18nKeyIntroductionImage3String = '';
  state.i18nKeyIntroductionImage4String = '';
  state.i18nKeyIntroductionMovieString = '';
  state.i18nKeyIntroductionDescriptionArray = '';
  state.i18nKeyShootTitleString = '';
  state.i18nKeyShootDescriptionArray = '';
  state.i18nKeyConfirmationListArray = '';
  state.i18nKeyErrorTitleString = '';
  state.pictureWidth = 0;
  state.pictureHeight = 0;
  state.displayWidth = 0;
  state.displayHeight = 0;
  state.clippingPictureWidth = 0;
  state.clippingDisplayWidth = 0;
  state.pictureIdDocCenter = 0;
  state.displayIdDocCenter = 0;
  state.isNotFound = false;
  state.isResize = false;
  state.isStart = false;
  state.isShooting = false;
  state.triggerShoot = false;
  state.triggerDetect = 0;
  state.detectCount = 0;
  state.isPermittedCameraAccess = false;
  state.isDetectableIdDoc = false;
  state.isDetectStop = false;
  state.isDetectStart = false;
  state.isDetectCapture = false;
  state.isDetectSuccess = false;
  state.detectWarningCode = '';
  state.retryCount = 0;
  state.photoID = {};
  state.trimmedPhoto = {};
  state.videoDevices = [];
  state.actualFacingMode = 'none';
  state.isCameraChanging = false;
  state.selectedDeviceIndex = {
    [FacingMode.Environment]: 0,
    [FacingMode.User]: 0
  };
  state.unavailableCameras = [];
  state.showCameraModeModal = false;
  state.isRefreshRequesting = false;
}

export const getters: GetterTree<State, RootState> = {
  isPC: (state: State): boolean => getOSType() === OsType.PC,
  isAndroid: (state: State): boolean => getOSType() === OsType.Android,
  idDocType: (state: State): IdDocType => state.idDocType,
  mnDocType: (state: State): MnDocType => state.mnDocType,
  gpDocType: (state: State): string => state.gpDocType,
  opDocType: (state: State): string[] => state.opDocType,
  isPermittedCameraAccess: (state: State): boolean => state.isPermittedCameraAccess,
  isGpdoc: (state: State): boolean => state.shootType === 'general-purpose-document-page',
  isOpdoc: (state: State): boolean => state.shootType === 'optional-document-page',
  gpdocPageCurr: (state: State): number => state.gpDocPageCurr,
  gpdocPageLast: (state: State): number => state.gpDocPageLast,
  opdocTermCurr: (state: State): number => state.opDocTermCurr,
  opdocTermLast: (state: State): number => state.opDocTermLast,
  opdocPageCurr: (state: State): number => state.opDocPageCurr,
  opdocPageLast: (state: State): number => state.opDocPageLast,
  // 紙書類
  isGeneralBook: (state): boolean => {
    switch (state.idDocType) {
      case IdDocType.PhysicalDisabilityCertificateBookTo:
      case IdDocType.PhysicalDisabilityCertificateBookTi:
      case IdDocType.SpecialEducationBookTo:
      case IdDocType.SpecialEducationBookTi:
      case IdDocType.MentalDisabilityCertificateBookTo:
      case IdDocType.MentalDisabilityCertificateBookTi:
      case IdDocType.PensionBookTo:
      case IdDocType.PensionBookTi:
      case IdDocType.BasicPensionNumberNotificationTo:
      case IdDocType.BasicPensionNumberNotificationTi:
      case IdDocType.StudentNotebookTo:
      case IdDocType.StudentNotebookTi:
        return true;
      default:
        return false;
    }
  },
  isGeneralCard: (state): boolean => {
    switch (state.idDocType) {
      case IdDocType.PhysicalDisabilityCertificateCardTo:
      case IdDocType.PhysicalDisabilityCertificateCardTi:
      case IdDocType.SpecialEducationCardTo:
      case IdDocType.SpecialEducationCardTi:
      case IdDocType.MentalDisabilityCertificateCardTo:
      case IdDocType.MentalDisabilityCertificateCardTi:
      case IdDocType.BasicResidentRegistrationCardTo:
      case IdDocType.BasicResidentRegistrationCardTi:
      case IdDocType.StudentIDCardTo:
      case IdDocType.StudentIDCardTi:
        return true;
      default:
        return false;
    }
  },
  isGlobal: (state): boolean => {
    switch (state.idDocType) {
      case IdDocType.DriversLicenseGlobalNoFace:
      case IdDocType.NationalIdentityCardGlobalNoFace:
      case IdDocType.ResidenceCardGlobalNoFace:
      case IdDocType.PassportGlobalNoFace:
      case IdDocType.DriversLicenseGlobalFace:
      case IdDocType.NationalIdentityCardGlobalFace:
      case IdDocType.ResidenceCardGlobalFace:
      case IdDocType.PassportGlobalFace:
      case IdDocType.PhilIDGlobalFace:
      case IdDocType.TINIDGlobalFace:
      case IdDocType.SSSIDGlobalFace:
      case IdDocType.UMIDGlobalFace:
      case IdDocType.UMID2GlobalFace:
        return true;
      default:
        return false;
    }
  },
  isBookType: (state, _, __, rootGetters): boolean => {
    if (rootGetters['camera/isGeneralBook']) return true;
    switch (state.idDocType) {
      case IdDocType.PassportHo:
      case IdDocType.PassportTo:
      case IdDocType.PassportTi:
      case IdDocType.PassportGlobalFace:
      case IdDocType.PassportAnyIssuanceDateTi:
      case IdDocType.PassportAnyIssuanceDateTo:
      case IdDocType.PassportGlobalNoFace:
        return true;
      default:
        return false;
    }
  },
  isIdDocDiagonal: (state): boolean => state.shootType === 'identity-document-diagonal',
  isIdDocFront: (state): boolean => state.shootType === 'identity-document-front',
  isIdDocBack: (state): boolean => state.shootType === 'identity-document-back',

  facingMode: (state: State, getters): VideoFacingModeEnum | 'none' => {
    const isIdDocFront: boolean = state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = state.shootType === 'face-front';
    const isFaceAction: boolean = state.shootType === 'face-action';
    if (isIdDocFront) return 'environment';
    if (isIdDocDiagonal) return 'environment';
    if (isIdDocBack) return 'environment';
    if (isMnDocFront) return 'environment';
    if (isMnDocBack) return 'environment';
    if (getters['isGpdoc']) return 'environment';
    if (getters['isOpdoc']) return 'environment';
    if (isFaceFront) return 'user';
    if (isFaceAction) return 'user';
    return 'none';
  },

  displayScale: (state: State, getters, __, rootGetters): number => {
    if (!rootGetters['auth/kycOrgConfigEnableShrinkFrame']) return 1.0;
    const isPC: boolean = getters['isPC'];
    const isFaceFront: boolean = state.shootType === 'face-front';
    const isFaceAction: boolean = state.shootType === 'face-action';
    if (isPC || isFaceFront || isFaceAction) return 1.0;
    return 1.2;
  },

  isMirror: (state: State, getters): boolean => {
    if (state.actualFacingMode === 'user') return true;
    return getters['facingMode'] === 'user';
  },

  // カメラモードの手動切り替えができるかどうか
  canChangeCameraModeManually: (): boolean => {
    // 現時点ではAndroidの場合にのみ手動切り替えをOnにする
    return getOSType() === OsType.Android;
  },
  faceOvalWidth: (_, getters): number => Math.ceil(getters['faceOvalHeight'] * (7 / 9)),
  faceOvalHeight: (state: State): number => {
    // 画面横サイズに比例したマスクの高さを計算
    const preMaskWidth: number = Math.ceil(state.displayWidth * 0.7);
    const maskHeight1: number = Math.ceil(preMaskWidth * (9 / 7));
    // 画面縦サイズに比例したマスクの高さを計算
    const maskHeight2: number = Math.ceil(state.displayHeight * (5 / 8));
    // 画面縦の中央にマスクをおく上で全てを表示するためにとりうる最大の高さを計算
    const imaginaryHeaderFooterHeight: number = 100;
    const maxHeight: number = Math.max(0, state.displayHeight - imaginaryHeaderFooterHeight * 2);
    // 上記高さのうち小さい方を採用
    return Math.min(maskHeight1, maskHeight2, maxHeight);
  },

  locationParams: (state: State, getters): { [key: string]: string } => {
    const routeParams: { [key: string]: string } = {};
    const isGpDocUnknown: boolean = state.gpDocType === 'unknown';
    const isOpDocUnknown: boolean = state.opDocType.length <= 0;
    if (!isGpDocUnknown) routeParams['gpdoc_page'] = `${state.gpDocPageCurr}`;
    if (!isOpDocUnknown) routeParams['opdoc_term'] = `${state.opDocTermCurr}`;
    if (!isOpDocUnknown) routeParams['opdoc_page'] = `${state.opDocPageCurr}`;
    return routeParams;
  },

  locationFirst: (state: State, getters): Location => {
    const isIdDocMnGeneral: boolean = state.idDocType === IdDocType.MnGeneral;
    const isIdDocGpGeneral: boolean = state.idDocType === IdDocType.GpGeneral;
    const isMnDocUnknown: boolean = state.mnDocType === MnDocType.Unknown;
    const isGpDocUnknown: boolean = state.gpDocType === 'unknown';
    let routeName: string = '';

    const routeParams: { [key: string]: string } = Object.assign({}, getters['locationParams']);
    if (!isIdDocMnGeneral && !isIdDocGpGeneral) {
      // 通常の本人確認書類からの撮影開始
      routeName = 'identity-document-front-introduction';
    } else if (isIdDocMnGeneral && !isMnDocUnknown) {
      // 個人番号確認書類からの撮影開始
      routeName = 'mynumber-document-front-introduction';
    } else if (isIdDocGpGeneral && !isGpDocUnknown) {
      // 汎用書類からの撮影開始
      routeName = 'general-purpose-document-page-introduction';
      routeParams['gpdoc_page'] = '1';
    } else {
      // とりあえず完了 ここには来ないはず
      routeName = 'application-completion';
    }

    return { name: routeName, params: routeParams };
  },

  locationIntroduction: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-introduction`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationShoot: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-shooting`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationConfirmation: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-confirmation`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationError: (state: State, getters): Location => {
    const routeName: string = `${state.shootType}-error`;
    return { name: routeName, params: getters['locationParams'] };
  },

  locationNext: (state: State, getters): Location => {
    let routeName: string = '';
    const routeParams: { [key: string]: string } = Object.assign({}, getters['locationParams']);

    if (getters['isGpdoc']) {
      if (state.gpDocPageCurr < state.gpDocPageLast) {
        routeName = `general-purpose-document-page-shooting`;
        routeParams['gpdoc_page'] = `${state.gpDocPageCurr + 1}`;
      } else {
        return getters['locationSkip'];
      }
    } else if (getters['isOpdoc']) {
      if (state.opDocPageCurr < state.opDocPageLast) {
        routeName = `optional-document-page-shooting`;
        routeParams['opdoc_page'] = `${state.opDocPageCurr + 1}`;
      } else {
        return getters['locationSkip'];
      }
    } else {
      const isIdDocFront: boolean = state.shootType === 'identity-document-front';
      const isIdDocDiagonal: boolean = state.shootType === 'identity-document-diagonal';
      const isIdDocBack: boolean = state.shootType === 'identity-document-back';
      const isMnDocFront: boolean = state.shootType === 'mynumber-document-front';
      const isMnDocBack: boolean = state.shootType === 'mynumber-document-back';
      const isFaceFront: boolean = state.shootType === 'face-front';
      const isFaceAction: boolean = state.shootType === 'face-action';

      const isIdDocMyNumberCardHo: boolean = state.idDocType === IdDocType.MyNumberCardHo;
      const isIdDocMyNumberCardTo: boolean = state.idDocType === IdDocType.MyNumberCardTo;
      const isIdDocMyNumberCardTi: boolean = state.idDocType === IdDocType.MyNumberCardTi;
      const isMnDocResidenceCertificate: boolean = state.mnDocType === MnDocType.ResidenceCertificate;
      const isIdDocMyNumberCard: boolean = isIdDocMyNumberCardHo || isIdDocMyNumberCardTo || isIdDocMyNumberCardTi;

      const isIdDocUnknown: boolean = state.idDocType === IdDocType.Unknown;
      const isMnDocUnknown: boolean = state.mnDocType === MnDocType.Unknown;
      const isOpDocUnknown: boolean = state.opDocType.length <= 0;

      // 本人確認書類斜面撮影スキップフラグ
      let isSkipIdDocDiagonal: boolean = false;
      if (getters['isGlobal']) isSkipIdDocDiagonal = true;

      // 本人確認書類裏面撮影スキップフラグ
      let isSkipIdDocBack: boolean = false;
      if (isIdDocMyNumberCard) isSkipIdDocBack = true;
      if (state.idDocType === IdDocType.PassportGlobalNoFace) isSkipIdDocBack = true;
      if (state.idDocType === IdDocType.PassportGlobalFace) isSkipIdDocBack = true;
      if (state.idDocType === IdDocType.TINIDGlobalFace) isSkipIdDocBack = true;
      if (state.idDocType === IdDocType.SSSIDGlobalFace) isSkipIdDocBack = true;
      if (state.idDocType === IdDocType.UMIDGlobalFace) isSkipIdDocBack = true;
      if (state.idDocType === IdDocType.UMID2GlobalFace) isSkipIdDocBack = true;

      // 顔撮影フラグ
      let isStepFace: boolean = false;
      if (state.idDocType === IdDocType.DriversLicenseHo) isStepFace = true;
      if (state.idDocType === IdDocType.MyNumberCardHo) isStepFace = true;
      if (state.idDocType === IdDocType.ResidenceCardHo) isStepFace = true;
      if (state.idDocType === IdDocType.SpecialPermanentResidentCertificateHo) isStepFace = true;
      if (state.idDocType === IdDocType.PassportHo) isStepFace = true;
      if (state.idDocType === IdDocType.DrivingHistoryCertificateHo) isStepFace = true;
      if (state.idDocType === IdDocType.DriversLicenseGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.NationalIdentityCardGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.ResidenceCardGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.PassportGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.PhilIDGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.TINIDGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.SSSIDGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.UMIDGlobalFace) isStepFace = true;
      if (state.idDocType === IdDocType.UMID2GlobalFace) isStepFace = true;

      // 遷移先設定
      if (isIdDocFront) routeName = 'identity-document-diagonal-introduction';
      if (isIdDocDiagonal) routeName = 'identity-document-back-introduction';
      if (isIdDocBack) routeName = 'mynumber-document-front-introduction';
      if (isMnDocFront) routeName = 'mynumber-document-back-introduction';
      if (isMnDocBack) routeName = 'face-front-introduction';
      if (isFaceFront) routeName = 'face-action-introduction';
      if (isFaceAction) routeName = 'optional-document-page-introduction';
      // 本人確認書類の撮影が必要なければスキップ
      if (isIdDocUnknown && isIdDocFront) routeName = '';
      if (isIdDocUnknown && isIdDocDiagonal) routeName = '';
      if (isIdDocUnknown && isIdDocBack) routeName = '';
      if (isIdDocUnknown && routeName === 'identity-document-front-introduction') routeName = 'mynumber-document-front-introduction'; // eslint-disable-line prettier/prettier
      // スキップフラグを確認して本人確認書類斜面を省略
      if (isSkipIdDocDiagonal && isIdDocDiagonal) routeName = '';
      if (isSkipIdDocDiagonal && routeName === 'identity-document-diagonal-introduction')
        routeName = 'identity-document-back-introduction';
      // スキップフラグを確認して本人確認書類裏面を省略
      if (isSkipIdDocBack && isIdDocBack) routeName = '';
      if (isSkipIdDocBack && routeName === 'identity-document-back-introduction') routeName = 'mynumber-document-front-introduction'; // eslint-disable-line prettier/prettier
      // 個人番号書類の撮影が必要なければスキップ
      if (isMnDocUnknown && isMnDocFront) routeName = '';
      if (isMnDocUnknown && isMnDocBack) routeName = '';
      if (isMnDocUnknown && routeName === 'mynumber-document-front-introduction') routeName = 'face-front-introduction';
      // 住民票では裏面を省略
      if (isMnDocResidenceCertificate && isMnDocBack) routeName = '';
      if (isMnDocResidenceCertificate && routeName === 'mynumber-document-back-introduction') routeName = 'face-front-introduction'; // eslint-disable-line prettier/prettier
      // 顔撮影の必要がなければスキップ
      if (!isStepFace && isFaceFront) routeName = '';
      if (!isStepFace && isFaceAction) routeName = '';
      if (!isStepFace && routeName === 'face-front-introduction') routeName = 'optional-document-page-introduction';
      // スキップフラグを確認して補助書類撮影を省略
      if (isOpDocUnknown && routeName === 'optional-document-page-introduction') routeName = 'application-completion';

      // 補助書類撮影の場合は最初から
      if (routeName === 'optional-document-page-introduction') {
        routeParams['opdoc_term'] = '1';
        routeParams['opdoc_page'] = '1';
      }
    }

    return { name: routeName, params: routeParams };
  },

  locationSkip: (state: State, getters): Location => {
    let routeName: string = '';
    const routeParams: { [key: string]: string } = Object.assign({}, getters['locationParams']);

    if (getters['isOpdoc'] && state.opDocTermCurr < state.opDocTermLast) {
      routeName = `optional-document-page-introduction`;
      routeParams['opdoc_term'] = `${state.opDocTermCurr + 1}`;
      routeParams['opdoc_page'] = '1';
    } else {
      routeName = 'application-completion';
    }

    return { name: routeName, params: routeParams };
  },

  availableVideoDevices: (state: State): CameraDevice[] => {
    return state.videoDevices.filter(device => state.unavailableCameras.includes(device.deviceId) === false);
  }
};

export const mutations: MutationTree<State> = {
  setIdDocType(state: State, payload: IdDocType): void {
    state.idDocType = payload;
  },

  setMnDocType(state: State, payload: MnDocType): void {
    state.mnDocType = payload;
  },

  setGpDocType(state: State, payload: string): void {
    state.gpDocType = payload;
  },

  setOpDocType(state: State, payload: string[]): void {
    state.opDocType = payload;
    state.opDocTermLast = payload.length;
  },

  setShootType(state: State, payload: ShootType): void {
    state.shootType = payload;
  },

  setGpDocPageCurr(state: State, payload: number): void {
    state.gpDocPageCurr = payload;
  },

  setGpDocPageLast(state: State, payload: number): void {
    state.gpDocPageLast = payload;
  },

  setOpDocTermCurr(state: State, payload: number): void {
    state.opDocTermCurr = payload;
  },

  setOpDocPageCurr(state: State, payload: number): void {
    state.opDocPageCurr = payload;
  },

  setOpDocPageLast(state: State, payload: number): void {
    state.opDocPageLast = payload;
  },

  setMountedSettingSize(
    state: State,
    payload: { isPC: boolean; isGpDoc: boolean; isOpDoc: boolean; isGeneralBook: boolean }
  ): void {
    const isIdDocFront: boolean = state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = state.shootType === 'mynumber-document-front';

    const isIdDocPassportHo: boolean = state.idDocType === IdDocType.PassportHo;
    const isIdDocPassportTo: boolean = state.idDocType === IdDocType.PassportTo;
    const isIdDocPassportTi: boolean = state.idDocType === IdDocType.PassportTi;
    const isIdDocSpecialPermanentResidentCertificateHo: boolean =
      state.idDocType === IdDocType.SpecialPermanentResidentCertificateHo;
    const isIdDocSpecialPermanentResidentCertificateTo: boolean =
      state.idDocType === IdDocType.SpecialPermanentResidentCertificateTo;
    const isIdDocSpecialPermanentResidentCertificateTi: boolean =
      state.idDocType === IdDocType.SpecialPermanentResidentCertificateTi;
    const isIdDocPassportAnyIssuanceDateTo: boolean = state.idDocType === IdDocType.PassportAnyIssuanceDateTo;
    const isIdDocPassportAnyIssuanceDateTi: boolean = state.idDocType === IdDocType.PassportAnyIssuanceDateTi;
    const isMnDocResidenceCertificate: boolean = state.mnDocType === MnDocType.ResidenceCertificate;

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isGpDoc: boolean = payload.isGpDoc;
    const isOpDoc: boolean = payload.isOpDoc;
    const isGeneralBook: boolean = payload.isGeneralBook;
    const isIdDocPassport: boolean =
      isIdDocPassportHo ||
      isIdDocPassportTo ||
      isIdDocPassportTi ||
      isIdDocPassportAnyIssuanceDateTo ||
      isIdDocPassportAnyIssuanceDateTi;
    const isIdDocSpecialPermanentResidentCertificate: boolean =
      isIdDocSpecialPermanentResidentCertificateHo ||
      isIdDocSpecialPermanentResidentCertificateTo ||
      isIdDocSpecialPermanentResidentCertificateTi;

    state.isDetectStart = isIdDoc && state.isDetectableIdDoc && !state.isDetectStop;

    // ヘッダとフッダの高さ
    state.headerHeight = payload.isPC ? 80 : state.isDetectStart ? 70 : 50;
    state.shootFooterHeight = payload.isPC ? 140 : state.isDetectStart ? 0 : 100;
    // ヘッダの追加マージン 斜め撮影では画面上に文言の追加 自動撮影ではメッセージボックス分の余裕
    state.headerMarginHeight = isIdDocDiagonal && !isGeneralBook ? 57 : state.isDetectStart ? 40 : 0;
    // 手動斜め撮影の時は、ボタンの位置が変わる (パスポート/特別永住者証明書は除く)
    if (
      !state.isDetectStart &&
      isIdDocDiagonal &&
      !payload.isPC &&
      !isIdDocPassport &&
      !isIdDocSpecialPermanentResidentCertificate &&
      !isGeneralBook
    )
      state.shootFooterHeight = 160;

    state.idDocAspectRatioView = 0.63 * (isIdDocDiagonal ? 0.8 : 1.0);
    if (isIdDocFront && isIdDocPassport) state.idDocAspectRatioView = 0.7;
    if (isIdDocDiagonal && isIdDocPassport) state.idDocAspectRatioView = 0.75;
    if (isIdDocBack && isIdDocPassport) state.idDocAspectRatioView = 0.7;
    if (isMnDocFront && isMnDocResidenceCertificate) state.idDocAspectRatioView = 1.41;
    if (isGpDoc || isOpDoc || isGeneralBook) state.idDocAspectRatioView = 1.0;
    // 本人確認書類は撮影位置をサーバから受け取る必要があり、その計算に必要なカード縦横比をサーバと合わせるために、表示用と別の値を用意する
    state.idDocAspectRatioCalc = state.idDocAspectRatioView;
    if (isIdDoc && !isGeneralBook) state.idDocAspectRatioCalc = isIdDocPassport ? 0.7 : 0.63;
  },

  setMountedSettingI18nKey(
    state: State,
    payload: { isPC: boolean; isGpDoc: boolean; isOpDoc: boolean; isGlobal: boolean; regionCode: RegionCode }
  ): void {
    const { isGlobal, isGpDoc, isOpDoc, isPC, regionCode } = payload;
    const isIdDocFront: boolean = state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = state.shootType === 'face-front';
    const isFaceAction: boolean = state.shootType === 'face-action';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isMnDoc: boolean = isMnDocFront || isMnDocBack;
    const isFace: boolean = isFaceFront || isFaceAction;

    /**
     * @TODO UtilにidDocの分岐を集約する
     */
    const isGeneralBook = (idDocType: IdDocType): boolean => {
      switch (idDocType) {
        case IdDocType.PhysicalDisabilityCertificateBookTo:
        case IdDocType.PhysicalDisabilityCertificateBookTi:
        case IdDocType.SpecialEducationBookTo:
        case IdDocType.SpecialEducationBookTi:
        case IdDocType.MentalDisabilityCertificateBookTo:
        case IdDocType.MentalDisabilityCertificateBookTi:
        case IdDocType.PensionBookTo:
        case IdDocType.PensionBookTi:
        case IdDocType.BasicPensionNumberNotificationTo:
        case IdDocType.BasicPensionNumberNotificationTi:
        case IdDocType.StudentNotebookTo:
        case IdDocType.StudentNotebookTi:
          return true;
        default:
          return false;
      }
    };
    const isGeneralCard = (idDocType: IdDocType): boolean => {
      switch (idDocType) {
        case IdDocType.PhysicalDisabilityCertificateCardTo:
        case IdDocType.PhysicalDisabilityCertificateCardTi:
        case IdDocType.SpecialEducationCardTo:
        case IdDocType.SpecialEducationCardTi:
        case IdDocType.MentalDisabilityCertificateCardTo:
        case IdDocType.MentalDisabilityCertificateCardTi:
        case IdDocType.BasicResidentRegistrationCardTo:
        case IdDocType.BasicResidentRegistrationCardTi:
        case IdDocType.StudentIDCardTo:
        case IdDocType.StudentIDCardTi:
          return true;
        default:
          return false;
      }
    };

    // 言語により切り替わる情報のキー設定
    let category: string = '';
    let document: string = '';
    let shootCode: string = state.shootType;
    let documentKey: string = '';
    if (isIdDoc) {
      category = 'identity-document';
      document = {
        [IdDocType.DriversLicenseHo]: 'drivers-license',
        [IdDocType.DriversLicenseTo]: 'drivers-license',
        [IdDocType.DriversLicenseTi]: 'drivers-license',
        [IdDocType.MyNumberCardHo]: 'my-number-card',
        [IdDocType.MyNumberCardTo]: 'my-number-card',
        [IdDocType.MyNumberCardTi]: 'my-number-card',
        [IdDocType.ResidenceCardHo]: 'residence-card',
        [IdDocType.ResidenceCardTo]: 'residence-card',
        [IdDocType.ResidenceCardTi]: 'residence-card',
        [IdDocType.SpecialPermanentResidentCertificateHo]: 'special-permanent-resident-certificate',
        [IdDocType.SpecialPermanentResidentCertificateTo]: 'special-permanent-resident-certificate',
        [IdDocType.SpecialPermanentResidentCertificateTi]: 'special-permanent-resident-certificate',
        [IdDocType.PassportHo]: 'passport',
        [IdDocType.PassportTo]: 'passport',
        [IdDocType.PassportTi]: 'passport',
        [IdDocType.DrivingHistoryCertificateHo]: 'driving-history-certificate',
        [IdDocType.DrivingHistoryCertificateTo]: 'driving-history-certificate',
        [IdDocType.DrivingHistoryCertificateTi]: 'driving-history-certificate',
        [IdDocType.HealthInsuranceCardTo]: 'health-insurance-card',
        [IdDocType.HealthInsuranceCardTi]: 'health-insurance-card',
        [IdDocType.PhysicalDisabilityCertificateBookTo]: 'physical-disability-certificate-book',
        [IdDocType.PhysicalDisabilityCertificateCardTo]: 'physical-disability-certificate-card',
        [IdDocType.SpecialEducationBookTo]: 'special-education-book',
        [IdDocType.SpecialEducationCardTo]: 'special-education-card',
        [IdDocType.StudentNotebookTo]: 'student-notebook',
        [IdDocType.MentalDisabilityCertificateBookTo]: 'mental-disability-certificate-book',
        [IdDocType.MentalDisabilityCertificateCardTo]: 'mental-disability-certificate-card',
        [IdDocType.PensionBookTo]: 'pension-book',
        [IdDocType.BasicPensionNumberNotificationTo]: 'basic-pension-number-notification',
        [IdDocType.PassportAnyIssuanceDateTo]: 'passport-any-issuance-date',
        [IdDocType.BasicResidentRegistrationCardTo]: 'basic-resident-registration-card',
        [IdDocType.StudentIDCardTo]: 'student-id-card',
        [IdDocType.PhysicalDisabilityCertificateBookTi]: 'physical-disability-certificate-book',
        [IdDocType.PhysicalDisabilityCertificateCardTi]: 'physical-disability-certificate-card',
        [IdDocType.SpecialEducationBookTi]: 'special-education-book',
        [IdDocType.SpecialEducationCardTi]: 'special-education-card',
        [IdDocType.StudentNotebookTi]: 'student-notebook',
        [IdDocType.MentalDisabilityCertificateBookTi]: 'mental-disability-certificate-book',
        [IdDocType.MentalDisabilityCertificateCardTi]: 'mental-disability-certificate-card',
        [IdDocType.PensionBookTi]: 'pension-book',
        [IdDocType.BasicPensionNumberNotificationTi]: 'basic-pension-number-notification',
        [IdDocType.PassportAnyIssuanceDateTi]: 'passport-any-issuance-date',
        [IdDocType.StudentIDCardTi]: 'student-id-card',
        [IdDocType.BasicResidentRegistrationCardTi]: 'basic-resident-registration-card',
        [IdDocType.DriversLicenseGlobalNoFace]: 'drivers-license-global-no-face',
        [IdDocType.NationalIdentityCardGlobalNoFace]: 'national-identity-card-global-no-face',
        [IdDocType.ResidenceCardGlobalNoFace]: 'residence-card-global-no-face',
        [IdDocType.PassportGlobalNoFace]: 'passport-global-no-face',
        [IdDocType.DriversLicenseGlobalFace]: 'drivers-license-global-face',
        [IdDocType.NationalIdentityCardGlobalFace]: 'national-identity-card-global-face',
        [IdDocType.ResidenceCardGlobalFace]: 'residence-card-global-face',
        [IdDocType.PassportGlobalFace]: 'passport-global-face',
        [IdDocType.PhilIDGlobalFace]: 'philid-global-face',
        [IdDocType.TINIDGlobalFace]: 'tinid-global-face',
        [IdDocType.SSSIDGlobalFace]: 'sssid-global-face',
        [IdDocType.UMIDGlobalFace]: 'umid-global-face',
        [IdDocType.UMID2GlobalFace]: 'umid2-global-face',
        [IdDocType.MnGeneral]: 'unknown',
        [IdDocType.GpGeneral]: 'unknown',
        [IdDocType.Unknown]: 'unknown'
      }[state.idDocType];

      /**
       * 汎用手帳・カード系は共通のi18nファイル（general-book, general-card）に記載されている
       * ただし書類名はそれぞれの書類によって異なるため、$t関数で呼び出すときに必要に応じで変数として書類名を渡す
       * 書類名は [documentKey]:{書類名の翻訳} で定義している
       * @example src/i18n/ja/camera/identity-document/general-book/doucmentName.json
       */
      if (isGeneralBook(state.idDocType) && document !== 'student-notebook') {
        documentKey = document;
        document = 'general-book';
      }

      if (isGeneralCard(state.idDocType)) {
        documentKey = document;
        document = 'general-card';
      }
    } else if (isMnDoc) {
      category = 'mynumber-document';
      document = {
        [MnDocType.MyNumberCard]: 'my-number-card',
        [MnDocType.NotificationCard]: 'notification-card',
        [MnDocType.ResidenceCertificate]: 'residence-certificate',
        [MnDocType.Unknown]: 'unknown'
      }[state.mnDocType];
    } else if (isGpDoc) {
      category = 'general-purpose-document';
      document = 'common';
      shootCode = 'general-purpose-document-i18n';
    } else if (isOpDoc) {
      category = 'optional-document';
      document = 'common';
      shootCode = 'optional-document-i18n';
    } else if (isFace) {
      category = 'face';
      document = 'common';
    }

    const operationType: string = state.isDetectStart ? 'autodetect' : 'manual';
    const stepKeys: string[] = [];
    stepKeys.push(category);
    if (isGlobal && isIdDoc) stepKeys.push(`global.${regionCode}`);
    stepKeys.push(document);
    stepKeys.push(operationType);
    stepKeys.push(shootCode);
    const stepKey: string = stepKeys.join('.');

    const deviceKey: string = isPC ? 'Pc' : 'Sp';
    if (documentKey)
      state.documentName = vuei18n.t(
        `camera.descriptions.${category}.${document}.documentName.${documentKey}`
      ) as string;
    state.i18nKeyIntroductionTitleString = `camera.descriptions.${stepKey}.introductionTitle`;
    state.i18nKeyIntroductionCaptionHeaderString = `camera.descriptions.${stepKey}.introductionCaptionHeader`;
    state.i18nKeyIntroductionCaptionString = `camera.descriptions.${stepKey}.introductionCaption`;
    state.i18nKeyIntroductionImage1String = `camera.descriptions.${stepKey}.introduction${deviceKey}Image1`;
    state.i18nKeyIntroductionImage2String = `camera.descriptions.${stepKey}.introduction${deviceKey}Image2`;
    state.i18nKeyIntroductionImage3String = `camera.descriptions.${stepKey}.introduction${deviceKey}Image3`;
    state.i18nKeyIntroductionImage4String = `camera.descriptions.${stepKey}.introduction${deviceKey}Image4`;
    state.i18nKeyIntroductionMovieString = `camera.descriptions.${stepKey}.introduction${deviceKey}Movie`;
    state.i18nKeyIntroductionDescriptionArray = `camera.descriptions.${stepKey}.introduction${deviceKey}Description`;
    state.i18nKeyShootTitleString = `camera.descriptions.${stepKey}.shootTitle`;
    state.i18nKeyShootDescriptionArray = `camera.descriptions.${stepKey}.shoot${deviceKey}Description`;
    state.i18nKeyConfirmationListArray = `camera.descriptions.${stepKey}.confirmationList`;
    state.i18nKeyErrorTitleString = `camera.descriptions.${stepKey}.errorTitle`;
  },

  clearSetup(state: State): void {
    state.pictureWidth = 0;
    state.pictureHeight = 0;
    state.displayWidth = 0;
    state.displayHeight = 0;
    state.clippingPictureWidth = 0;
    state.clippingDisplayWidth = 0;
    state.pictureIdDocCenter = 0;
    state.displayIdDocCenter = 0;
    state.i18nKeyIntroductionImage1String = '';
    state.i18nKeyIntroductionImage2String = '';
    state.i18nKeyIntroductionImage3String = '';
    state.i18nKeyIntroductionImage4String = '';
    state.i18nKeyIntroductionMovieString = '';
  },

  pictureWidth(state: State, payload: number): void {
    state.pictureWidth = payload;
  },

  pictureHeight(state: State, payload: number): void {
    state.pictureHeight = payload;
  },

  displayWidth(state: State, payload: number): void {
    state.displayWidth = payload;
  },

  displayHeight(state: State, payload: number): void {
    state.displayHeight = payload;
  },

  clippingPictureWidth(state: State, payload: number): void {
    state.clippingPictureWidth = payload;
  },

  clippingDisplayWidth(state: State, payload: number): void {
    state.clippingDisplayWidth = payload;
  },

  pictureIdDocCenter(state: State, payload: number): void {
    state.pictureIdDocCenter = payload;
  },

  displayIdDocCenter(state: State, payload: number): void {
    state.displayIdDocCenter = payload;
  },

  isNotFound(state: State, payload: boolean): void {
    state.isNotFound = payload;
  },

  isResize(state: State, payload: boolean): void {
    state.isResize = payload;
  },

  isStart(state: State, payload: boolean): void {
    state.isStart = payload;
  },

  isShooting(state: State, payload: boolean): void {
    state.isShooting = payload;
  },

  triggerShoot(state: State, payload: boolean): void {
    state.triggerShoot = payload;
  },

  triggerDetect(state: State): void {
    state.triggerDetect++;
  },

  addDetectCount(state: State): void {
    state.detectCount++;
  },

  resetDetectCount(state: State): void {
    state.detectCount = 0;
  },

  isPermittedCameraAccess(state: State, payload: boolean): void {
    state.isPermittedCameraAccess = payload;
  },

  isDetectableIdDoc(state: State, payload: boolean): void {
    state.isDetectableIdDoc = payload;
  },

  isDetectStop(state: State, payload: boolean): void {
    state.isDetectStop = payload;
  },

  isDetectCapture(state: State, payload: boolean): void {
    state.isDetectCapture = payload;
  },

  isDetectSuccess(state: State, payload: boolean): void {
    state.isDetectSuccess = payload;
  },

  detectWarningCode(state: State, payload: string): void {
    state.detectWarningCode = payload;
  },

  addRetryCount(state: State): void {
    state.retryCount++;
  },

  resetRetryCount(state: State): void {
    state.retryCount = 0;
  },

  photoID(state: State, payload: { key: string; value: number }): void {
    state.photoID = Object.assign({}, state.photoID, {
      [payload.key]: payload.value
    });
  },

  trimmedPhoto(state: State, payload: { key: string; value: string }): void {
    state.trimmedPhoto = Object.assign({}, state.trimmedPhoto, {
      [payload.key]: payload.value
    });
  },

  clearState(state) {
    clearState(state);
  },

  setVideoDevices(state: State, payload: MediaDeviceInfo[]): void {
    state.videoDevices = payload.map(device => ({ deviceId: device.deviceId, label: device.label }));
  },

  setActualFacingMode(state: State, payload: VideoFacingModeEnum | 'none'): void {
    state.actualFacingMode = payload;
  },

  setIsCameraChanging(state: State, payload: boolean): void {
    state.isCameraChanging = payload;
  },

  setCameraModeIndex(state: State, payload: { mode: FacingMode; index: number }): void {
    state.selectedDeviceIndex[payload.mode] = payload.index;
  },

  setUnavailableCamera(state: State, payload: string): void {
    if (state.unavailableCameras.includes(payload) === false) {
      state.unavailableCameras.push(payload);
    }
  },

  setShowCameraModeModal(state: State, payload: boolean): void {
    state.showCameraModeModal = payload;
  },

  setIsRefreshRequesting(state: State, payload: boolean): void {
    state.isRefreshRequesting = payload;
  }
};

export const actions: ActionTree<State, RootState> = {
  clearState(context: ActionContext<State, RootState>): void {
    context.commit('clearState');
  },

  // ページマウント時の設定 パスから情報を設定する
  async mountedSetting(context: ActionContext<State, RootState>, payload: string): Promise<void> {
    const keys: string[] = payload.split('/');
    context.commit('setShootType', `${keys[1]}-${keys[2]}`);
    if (context.getters['isGpdoc']) context.commit('setGpDocPageCurr', parseInt(keys[3]));
    if (context.getters['isOpdoc']) context.commit('setOpDocTermCurr', parseInt(keys[3]));
    if (context.getters['isOpdoc']) context.commit('setOpDocPageCurr', parseInt(keys[4]));
    const isPC: boolean = context.getters['isPC'];
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];
    const isGeneralBook: boolean = context.getters['isGeneralBook'];
    const isGlobal: boolean = context.getters['isGlobal'];
    const regionCode: RegionCode = context.rootState.auth.regionCode;
    context.commit('setMountedSettingSize', { isPC, isGpDoc, isOpDoc, isGeneralBook });
    context.commit('setMountedSettingI18nKey', { isPC, isGpDoc, isOpDoc, isGlobal, regionCode });
    // 不正なページはエラーに飛ばす
    context.commit('isNotFound', context.getters['locationNext'].name === '');
  },

  // 撮影開始通信 ランダム枠の位置を取得する
  async storeStart(context: ActionContext<State, RootState>): Promise<void> {
    const isPC: boolean = context.getters['isPC'];

    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isMnDoc: boolean = isMnDocFront || isMnDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];
    const isGeneralBook: boolean = context.getters['isGeneralBook'];
    const isGlobal: boolean = context.getters['isGlobal'];

    if (isIdDoc || isMnDoc || isGpDoc || isOpDoc || isGeneralBook) {
      // 本人確認書類/個人番号書類撮影準備
      const displayScale: number = context.getters['displayScale'];
      const pictureWidth: number = context.state.pictureWidth / displayScale;
      const pictureHeight: number = context.state.pictureHeight / displayScale;
      const displayWidth: number = context.state.displayWidth;
      const displayHeight: number = context.state.displayHeight;

      // ヘッダとフッダの高さを計算
      let headerHeight: number = context.state.headerHeight + context.state.headerMarginHeight;
      let footerHeight: number = context.state.shootFooterHeight;
      if (isIdDoc && !isGeneralBook) {
        // 本人確認書類の自動斜め撮影の時は、本人確認書類が中央にくるようにする
        const headerFooterHeightHelf: number = Math.floor((headerHeight + footerHeight) / 2);
        if (context.state.isDetectStart && isIdDocDiagonal) headerHeight = footerHeight = headerFooterHeightHelf;

        // 中心から遠ざかるとピントがあいにくくなるようなので、移動範囲を80%に狭める
        // 移動範囲ができるだけ中央に来るようにする
        const movementRatio: number = isPC ? 1.0 : 0.8;
        const movementRange: number = (displayHeight - headerHeight - footerHeight) * movementRatio;
        let newHeaderHeight: number = (displayHeight - movementRange) / 2;
        let newFooterHeight: number = (displayHeight - movementRange) / 2;
        if (headerHeight > newHeaderHeight) {
          const headerDiff: number = headerHeight - newHeaderHeight;
          newFooterHeight = newFooterHeight - headerDiff;
          newHeaderHeight = newHeaderHeight + headerDiff;
        } else if (footerHeight > newFooterHeight) {
          const footerDiff: number = footerHeight - newFooterHeight;
          newFooterHeight = newFooterHeight + footerDiff;
          newHeaderHeight = newHeaderHeight - footerDiff;
        }
        headerHeight = newHeaderHeight;
        footerHeight = newFooterHeight;
      } else if (isMnDoc) {
        // 個人番号確認書類は中心に配置したいのでヘッダ/フッダを高い方に合わせて計算する
        headerHeight = footerHeight = Math.max(headerHeight, footerHeight);
      }
      // ヘッダとフッタの高さは0だとバリデーションに引っかかるので最小値は1にする
      if (headerHeight < 1) headerHeight = 1;
      if (footerHeight < 1) footerHeight = 1;

      // 画面と画像の比率を確認
      const aspectPicture: number = pictureWidth / pictureHeight;
      const aspectDisplay: number = displayWidth / displayHeight;
      const relativeRatioX: number = pictureWidth / displayWidth;
      const relativeRatioY: number = pictureHeight / displayHeight;
      const relativeRatio1: number = aspectPicture < aspectDisplay ? relativeRatioX : relativeRatioY;
      // 縦横のどちらかを画像と同じ大きさに拡大した場合の画面等の大きさ
      // 画面が画像に収まるように縦と横のどちらを合わせるかを決めている
      const pictureSpaceDisplayWidth: number = displayWidth * relativeRatio1;
      const pictureSpaceDisplayHeight: number = displayHeight * relativeRatio1;
      const pictureSpaceHeaderHeight: number = headerHeight * relativeRatio1;
      const pictureSpaceFooterHeight: number = footerHeight * relativeRatio1;

      if (isGpDoc || isOpDoc || isGeneralBook) {
        // 汎用書類のマーカーの大きさは、スマホ(縦長)の場合は画面に合わせ、PC(横長)の場合は正方形にする
        const pictureSpaceHeaderFooterHeight: number = pictureSpaceHeaderHeight + pictureSpaceFooterHeight;
        const pictureSpaceDocumentHeight: number = pictureSpaceDisplayHeight - pictureSpaceHeaderFooterHeight;
        context.state.idDocAspectRatioView = Math.max(1.0, pictureSpaceDocumentHeight / pictureSpaceDisplayWidth);
      }

      // 最小限の移動範囲を確保した上での本人確認書類/個人番号書類のとりうる最大の大きさ
      const pictureSpaceMinMovementRange: number = isIdDoc && !isGeneralBook ? 25 : 0;
      const pictureSpaceCardMaxHeight: number = pictureSpaceDisplayHeight - pictureSpaceHeaderHeight - pictureSpaceFooterHeight - pictureSpaceMinMovementRange; // eslint-disable-line prettier/prettier
      const pictureSpaceCardMaxWidth1: number = pictureSpaceCardMaxHeight / context.state.idDocAspectRatioCalc;
      // 見た目上の違和感を無くすために最大の大きさを設定
      const pictureSpaceCardMaxWidth2: number =
        isGpDoc || isOpDoc || isGeneralBook ? Number.POSITIVE_INFINITY : 1000 * relativeRatio1;
      // 画面縦幅よりもヘッダフッダ合計サイズの方が大きい場合は、横持ちや画面分割で異常なので、処理を打ち切る
      // TODO 撮影枠が小さすぎると撮影に支障が出るので、処理を打ち切るサイズを引き上げたい
      // TODO 処理を打ち切った際になんらかのログを残しておきたい
      if (pictureSpaceCardMaxHeight <= 0) throw new ApiCancelError('');

      // 本人確認書類が十分な移動量を確保するための画面/画像幅を計算
      const clippingPictureWidth: number = Math.floor(Math.min(pictureSpaceCardMaxWidth1, pictureSpaceCardMaxWidth2, pictureSpaceDisplayWidth)); // eslint-disable-line prettier/prettier
      const clippingDisplayWidth: number = Math.floor(clippingPictureWidth / relativeRatio1);

      let photoID: number = context.state.photoID[context.state.shootType] || -1;
      let positionY: number = 0.5;

      if (isIdDoc) {
        // 本人確認書類の画像IDとマーカー位置を取得する
        const response = await IdDocPhotos.location(
          {
            photo_type: isIdDocFront ? PhotoType.Front : isIdDocBack ? PhotoType.Back : PhotoType.Diagonal,
            photo: { width: clippingPictureWidth, height: pictureHeight },
            display: { width: clippingDisplayWidth, height: displayHeight },
            header: { width: clippingDisplayWidth, height: headerHeight },
            footer: { width: clippingDisplayWidth, height: footerHeight }
          },
          {
            card: isGeneralBook
              ? undefined
              : { width: clippingDisplayWidth, height: clippingDisplayWidth * context.state.idDocAspectRatioCalc },
            forceCenter: isGeneralBook || isGlobal
          }
        );
        photoID = response.data.idDocPhotoID;
        positionY = response.data.positionY;
      } else {
        // 画像サイズを基準とした高さを計算
        const pictureSpaceDisplayHeight: number = displayHeight * (clippingPictureWidth / clippingDisplayWidth);
        const pictureSpaceHeaderHeight: number = headerHeight * (clippingPictureWidth / clippingDisplayWidth);
        const pictureSpaceFooterHeight: number = footerHeight * (clippingPictureWidth / clippingDisplayWidth);
        const pictureSpaceCardHeight: number = clippingPictureWidth * context.state.idDocAspectRatioCalc;

        // カードサイズを考慮しない可動範囲計算
        const top: number = Math.max(0, (pictureHeight - pictureSpaceDisplayHeight) / 2 + pictureSpaceHeaderHeight);
        const bot: number = Math.max(0, (pictureHeight - pictureSpaceDisplayHeight) / 2 + pictureSpaceFooterHeight);
        // 下辺基準の値を上辺基準に変換する
        const bottom: number = pictureHeight - bot;

        const minRatio: number = (top + pictureSpaceCardHeight / 2) / pictureHeight;
        const maxRatio: number = (bottom - pictureSpaceCardHeight / 2) / pictureHeight;
        positionY = 0.5 * (maxRatio - minRatio) + minRatio;
      }

      // 撮影位置を計算
      const pictureIdDocCenter: number = positionY * pictureHeight;
      const relativeRatio2: number = clippingDisplayWidth / clippingPictureWidth;
      const displaySpacePictureHeight: number = pictureHeight * relativeRatio2;
      const displaySpacePictureCenter: number = pictureIdDocCenter * relativeRatio2;
      const displayIdDocCenter: number = displaySpacePictureCenter - (displaySpacePictureHeight - displayHeight) / 2;

      // 本人確認書類/個人番号書類撮影開始
      context.commit('clippingPictureWidth', clippingPictureWidth);
      context.commit('clippingDisplayWidth', clippingDisplayWidth);
      context.commit('pictureIdDocCenter', pictureIdDocCenter);
      context.commit('displayIdDocCenter', displayIdDocCenter);
      context.commit('photoID', { key: context.state.shootType, value: photoID });
    } else if (isFaceFront) {
      // 顔写真撮影開始
      context.commit('clippingPictureWidth', context.state.pictureWidth);
      context.commit('clippingDisplayWidth', context.state.displayWidth);
      context.commit('pictureIdDocCenter', context.state.pictureHeight * 0.5);
      context.commit('displayIdDocCenter', context.state.displayHeight * 0.5);
      context.commit('photoID', { key: context.state.shootType, value: -1 });
    }
  },

  // 撮影情報の送信 自動撮影
  async storeDetectData(context: ActionContext<State, RootState>, payload: string): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    if (isIdDocFront || isIdDocDiagonal || isIdDocBack) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await IdDocPhotos.autoDetect(photoID, { imageBase64: payload });
      // 枠内判定、画像大きさ判定、角度判定、ブレ判定、光反射判定、暗さ判定、判定が存在しない場合はundefined
      const resultFrame = response.data.cvResults.find(result => result.name === 'frame')?.result;
      const resultSize = response.data.cvResults.find(result => result.name === 'size')?.result;
      const resultDiagonal = response.data.cvResults.find(result => result.name === 'diagonal')?.result;
      const resultSharpness = response.data.cvResults.find(result => result.name === 'sharpness')?.result;
      const resultHighlight = response.data.cvResults.find(result => result.name === 'highlight')?.result;
      const resultBrightness = response.data.cvResults.find(result => result.name === 'brightness')?.result;
      // 判定に成功したらカウントアップし、失敗したらリセットする
      context.commit(response.data.isValid ? 'addDetectCount' : 'resetDetectCount');
      // 枠内判定、画像大きさ判定、角度判定のどれかが存在する場合、全てにおいて「存在しないもしくは判定成功」を満たしていたら枠を青くする
      const results: (boolean | undefined)[] = [resultFrame, resultSize, resultDiagonal];
      const isDetectCapturable: boolean = results.some(result => result !== undefined);
      const isDetectCapture: boolean = results.every(result => result !== false);
      if (isDetectCapturable) context.commit('isDetectCapture', isDetectCapture);
      if (!isDetectCapturable) context.commit('isDetectCapture', response.data.isValid);
      // 判定が存在してかつ判定失敗している場合にエラー文言を表示する 優先順位が高いほど下にある
      let detectWarningCode: string = '';
      if (resultBrightness === false) detectWarningCode = 'brightness';
      if (resultHighlight === false) detectWarningCode = 'highlight';
      if (resultSharpness === false) detectWarningCode = 'sharpness';
      if (!context.state.isDetectCapture) detectWarningCode = 'frame';
      context.commit('detectWarningCode', detectWarningCode);
    }
  },

  // 撮影情報の送信 手動/自動撮影
  async storeShootData(context: ActionContext<State, RootState>, payload: string): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];

    if (isIdDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await IdDocPhotos.upload(photoID, { imageBase64: payload, isAutoShoot: context.state.isDetectStart });
    } else if (isMnDocFront) {
      const response = await MnDocPhotos.upload({ imageBase64: payload, photoType: PhotoType.Front });
      context.commit('photoID', { key: context.state.shootType, value: response.data.myNumberDocPhotoId });
    } else if (isMnDocBack) {
      const response = await MnDocPhotos.upload({ imageBase64: payload, photoType: PhotoType.Back });
      context.commit('photoID', { key: context.state.shootType, value: response.data.myNumberDocPhotoId });
    } else if (isGpDoc) {
      const pageNumber: number = context.getters['gpdocPageCurr'];
      const response = await GpDocPhotos.upload({ imageBase64: payload, pageNumber });
      context.commit('photoID', { key: context.state.shootType, value: response.data.generalDocPhotoId });
    } else if (isOpDoc) {
      const optionalDocType: string = context.state.opDocType[context.getters['opdocTermCurr'] - 1];
      const pageNumber: number = context.getters['opdocPageCurr'];
      const response = await OpDocPhotos.upload({ imageBase64: payload, optionalDocType, pageNumber });
      context.commit('photoID', { key: context.state.shootType, value: response.data.optionalDocPhotoId });
    } else if (isFaceFront) {
      const response = await FaceFrontPhotos.upload({ imageBase64: payload });
      context.commit('photoID', { key: context.state.shootType, value: response.data.faceFrontPhotoID });
    }
  },

  // 撮影情報の取得
  async storeTrimmedPhotoData(context: ActionContext<State, RootState>): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isMnDoc: boolean = isMnDocFront || isMnDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];

    if (isIdDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await IdDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.trimmedImageBase64 });
    } else if (isMnDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await MnDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.trimmedImageBase64 });
    } else if (isGpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await GpDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.imageBase64 });
    } else if (isOpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await OpDocPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.imageBase64 });
    } else if (isFaceFront) {
      const photoID: number = context.state.photoID[context.state.shootType];
      const response = await FaceFrontPhotos.images(photoID);
      context.commit('trimmedPhoto', { key: context.state.shootType, value: response.data.imageBase64 });
    }
  },

  // 確認結果の送信
  async storeConfirm(context: ActionContext<State, RootState>): Promise<void> {
    const isIdDocFront: boolean = context.state.shootType === 'identity-document-front';
    const isIdDocDiagonal: boolean = context.state.shootType === 'identity-document-diagonal';
    const isIdDocBack: boolean = context.state.shootType === 'identity-document-back';
    const isMnDocFront: boolean = context.state.shootType === 'mynumber-document-front';
    const isMnDocBack: boolean = context.state.shootType === 'mynumber-document-back';
    const isFaceFront: boolean = context.state.shootType === 'face-front';

    const isIdDoc: boolean = isIdDocFront || isIdDocDiagonal || isIdDocBack;
    const isMnDoc: boolean = isMnDocFront || isMnDocBack;
    const isGpDoc: boolean = context.getters['isGpdoc'];
    const isOpDoc: boolean = context.getters['isOpdoc'];

    if (isIdDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await IdDocPhotos.confirm(photoID);
    } else if (isMnDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await MnDocPhotos.confirm(photoID);
    } else if (isGpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await GpDocPhotos.confirm(photoID);
    } else if (isOpDoc) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await OpDocPhotos.confirm(photoID);
    } else if (isFaceFront) {
      const photoID: number = context.state.photoID[context.state.shootType];
      await FaceFrontPhotos.confirm(photoID);
    }
  },

  useNextCameraMode(context: ActionContext<State, RootState>, payload: FacingMode): void {
    const videoDeviceLength = context.getters['availableVideoDevices'].length;

    if (videoDeviceLength <= 1) {
      context.commit('setShowCameraModeModal', true);
    }

    const nextIndex = context.state.selectedDeviceIndex[payload] + 1;
    if (nextIndex > videoDeviceLength - 1) {
      context.commit('setCameraModeIndex', { mode: payload, index: 0 });
    } else {
      context.commit('setCameraModeIndex', { mode: payload, index: nextIndex });
    }
  }
};

export const camera: Module<State, RootState> = {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions
};
